import { useEffect, useState } from 'react';
import { DATE_FORMATS, LOCALES, WEEK_STARTS, type Preferences } from '@forecastify/shared';
import { Button, Select } from '@forecastify/ui';
import { rememberLocale, useI18n, useT, type MessageKey } from '../../i18n';
import { Card, CardColumn, Field } from '../card';
import { zoneOptions } from '../timezones';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Preferences — language, zone, date format, week start.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The time-zone list comes from the RUNTIME, not from a hardcoded array.** The mockup ships
 * sixteen zones; somebody in Reykjavík is not a bug report. `Intl.supportedValuesOf('timeZone')`
 * is the browser's own IANA database, which is the same authority the *database* checks against
 * (`is_valid_timezone` asks Postgres to use the name) — so a zone the picker offers is a zone the
 * server accepts, without a third list to keep in step.
 *
 * ⚠️ **A filter box, because 400 zones in a `<select>` is unusable.** The mockup has one
 * (`usSetTzQuery`) and it is the right call.
 */

const DATE_FORMAT_SAMPLE: Readonly<Record<string, string>> = {
  'YYYY.MM.DD.': '2026.08.24.',
  'DD/MM/YYYY': '24/08/2026',
  'MM/DD/YYYY': '08/24/2026',
};

const LOCALE_LABEL: Readonly<Record<string, MessageKey>> = {
  hu: 'account.prefs.locale.hu',
  en: 'account.prefs.locale.en',
};

const WEEK_START_LABEL: Readonly<Record<string, MessageKey>> = {
  mon: 'account.prefs.weekStart.mon',
  sun: 'account.prefs.weekStart.sun',
};

/* ⚠️ `zonesFor` moved to `screens/timezones.ts` for item 49 — the person rail needs the same list,
   and two copies is how the two surfaces come to offer different zones. */

export function PreferencesSection({
  preferences,
  onSave,
  onDirtyChange,
}: {
  readonly preferences: Preferences;
  readonly onSave?: (patch: {
    locale: 'hu' | 'en';
    timezone: string;
    dateFormat: string;
    weekStart: string;
    theme: string;
    version: number;
  }) => Promise<void>;
  readonly onDirtyChange?: (dirty: boolean) => void;
}) {
  const t = useT();

  /* ⚠️ Shadowing avoided on purpose: the local state is the FORM's value and `setAppLocale` moves
   * the whole application. Two things called `setLocale` in one component is how a save ends up
   * changing the dropdown and nothing else. */
  const { setLocale: setAppLocale } = useI18n();
  const [locale, setLocale] = useState<'hu' | 'en'>(preferences.locale);
  const [timezone, setTimezone] = useState(preferences.timezone);
  const [dateFormat, setDateFormat] = useState<string>(preferences.dateFormat);
  const [weekStart, setWeekStart] = useState<string>(preferences.weekStart);
  const [saving, setSaving] = useState(false);

  const dirty =
    locale !== preferences.locale ||
    timezone !== preferences.timezone ||
    dateFormat !== preferences.dateFormat ||
    weekStart !== preferences.weekStart;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  /* ⚠️ The `zones` memo went with the filter box — `zoneOptions` builds the list and guarantees
     the stored zone is in it, which is the whole of what that memo was protecting. */

  return (
    <CardColumn>
      <Card
        title={t('account.prefs.title')}
        actions={
          dirty ? (
            <>
              <span className="text-muted-foreground text-[11.5px]">
                {t('account.section.unsaved')}
              </span>
              <Button
                size="sm"
                disabled={saving}
                onClick={() => {
                  if (onSave === undefined) return;
                  setSaving(true);
                  /**
                   * ┌───────────────────────────────────────────────────────────┐
                   * │ ⚠️⚠️ APPLIED IMMEDIATELY, EXACTLY AS THE THEME IS.        │
                   * └───────────────────────────────────────────────────────────┘
                   *
                   * `theme-section.tsx` paints before the save resolves and argues why: *"a theme
                   * that waits for a round trip feels broken, and it is the one setting where the
                   * preview IS the confirmation"*. A language is the second such setting — the
                   * labels around the button are the confirmation — and it is the one that had no
                   * effect at all until now, because nothing between sign-in and the first frame
                   * read `user_preferences.locale`.
                   *
                   * ⚠️ **Before the await, and not in a `.then`.** If the save fails the stored
                   * value is still the old one and the next load corrects the screen, which is the
                   * theme's trade; waiting for the round trip to swap the words would leave the
                   * dropdown showing a language the page is not in.
                   */
                  setAppLocale(locale);
                  rememberLocale(locale);
                  void onSave({
                    locale,
                    timezone,
                    dateFormat,
                    weekStart,
                    /* ⚠️ Carried through unchanged. The theme lives in the same row and is edited on
                     * its own tab — dropping it here would reset somebody's theme every time they
                     * saved a timezone. */
                    theme: preferences.theme,
                    version: preferences.version,
                  }).finally(() => {
                    setSaving(false);
                  });
                }}
              >
                {saving ? t('account.saving') : t('account.save')}
              </Button>
            </>
          ) : undefined
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('account.prefs.locale')}>
            <Select
              value={locale}
              options={LOCALES.map((value) => ({
                value,
                label: t(LOCALE_LABEL[value] ?? 'app.name'),
              }))}
              onChange={(event) => {
                setLocale(event.target.value as 'hu' | 'en');
              }}
            />
          </Field>

          <Field label={t('account.prefs.weekStart')}>
            <Select
              value={weekStart}
              options={WEEK_STARTS.map((value) => ({
                value,
                label: t(WEEK_START_LABEL[value] ?? 'app.name'),
              }))}
              onChange={(event) => {
                setWeekStart(event.target.value);
              }}
            />
          </Field>

          <Field
            label={t('account.prefs.dateFormat')}
            note={t('account.prefs.sample', { sample: DATE_FORMAT_SAMPLE[dateFormat] ?? '' })}
          >
            <Select
              value={dateFormat}
              /* ⚠️ The format strings are their own labels, and are NOT translated — `YYYY.MM.DD.`
               * is a pattern, not a sentence, and a localised version of it would be a different
               * pattern. Mono for the same reason a machine identifier is. */
              options={DATE_FORMATS.map((value) => ({ value, label: value }))}
              onChange={(event) => {
                setDateFormat(event.target.value);
              }}
            />
          </Field>

          {/**
           * ⚠️⚠️ **ONE SEARCHABLE DROPDOWN, WHERE THIS WAS A FILTER BOX ABOVE A `<select>` — item
           * 15.** The two-field arrangement existed because a native `<select>` of four hundred
           * zones is unusable and had no search of its own; item 23's `Select` has one, so the
           * filter box is a second control asking the same question one line higher.
           *
           * ⚠️ **The stale-filter trap goes with it.** The old pair could show a selected zone
           * that the filter excluded, or hide it — a `<select>` whose value is not among its
           * options renders blank, which reads as *"no zone set"*. `zoneOptions` guarantees the
           * stored one is present and the search runs inside the listbox.
           */}
          <Field label={t('account.prefs.timezone')}>
            <Select
              aria-label={t('account.prefs.timezone')}
              value={timezone}
              searchable
              searchPlaceholder={t('account.prefs.timezoneFilter')}
              options={zoneOptions(timezone)}
              onChange={(event) => {
                setTimezone(event.target.value);
              }}
            />
          </Field>
        </div>
      </Card>
    </CardColumn>
  );
}
